import { useEffect, useState } from "react";
import { BETA_SECRET, BETA_KEY, track } from "./lib/api";
import { useAuth } from "../components/AuthGate";
import { supabase } from "../lib/supabase";
import MessageThread from "../components/MessageThread";
import { useUnreadCount } from "../lib/useUnreadCount";
import {
  blankJourney,
  loadJourney,
  saveJourney,
  deleteJourney,
  listJourneys,
  getCurrentId,
  setCurrentId,
  getStageState,
  patchStage,
} from "./lib/journey";
import { DEMO_JOURNEY } from "./lib/demoJourney";
import { SELLER_CSS } from "./styles/seller";
import { STAGES, STAGE_COUNT } from "./stages";
import Logo from "./components/Logo";
import Intake from "./components/Intake";
import Dashboard from "./components/Dashboard";
import OwnershipConfirm from "./components/OwnershipConfirm";
import Stepper from "./components/Stepper";
import StageShell from "./components/StageShell";
import ChatJourney from "./components/ChatJourney";
import AskExpert from "./components/AskExpert";

// SellerApp — orchestrator with four views:
//
//   "dashboard"  → home; lists all in-flight listings + "Start new"
//   "intake"     → single-address entry for a new listing
//   "confirm"    → one-click soft ownership confirmation (post-intake gate)
//   "journey"    → the 12-step carousel for the active listing
//
// Journey state lives in localStorage (per-browser) under `sz_seller_listings`.
// Will migrate to server-side `seller_listings` table when Supabase Auth lands.

const computeInitialView = () => {
  const forcedView = new URLSearchParams(window.location.search).get("view");
  if (forcedView === "intake") {
    return { view: "intake", journey: null };
  }
  if (forcedView === "dashboard") {
    return { view: "dashboard", journey: null };
  }
  const id = getCurrentId();
  if (!id) return { view: "dashboard", journey: null };
  const j = loadJourney(id);
  if (!j) return { view: "dashboard", journey: null };
  // If we have a journey but haven't captured ownership confirmation yet,
  // route through the confirm gate (only happens once per journey).
  if (!j.verification) return { view: "confirm", journey: j };
  return { view: "journey", journey: j };
};

export default function SellerApp() {
  const isDemo = typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("demo") === "1";

  // Beta gate — bypassed in demo mode.
  if (!isDemo && typeof window !== "undefined" && localStorage.getItem(BETA_KEY) !== BETA_SECRET) {
    window.location.href = "/public";
    return null;
  }

  // Demo auto-advance: all 12 stages in order, durations in ms.
  // Total journey time ~110s + ~10s intake/confirm ≈ 2 minutes.
  const DEMO_SEQUENCE  = [0,     1,     2,     3,  4,  5,     6,    7,     8,     9,     10,    11];
  const DEMO_DURATIONS = [13000, 10000, 10000, 0,  0,  11000, 8000, 10000, 10000, 13000, 10000, 0];

  const auth = useAuth();
  const [{ view, journey }, setState] = useState(() =>
    isDemo ? { view: "intake", journey: null } : computeInitialView()
  );
  const [messagesOpen, setMessagesOpen] = useState(false);
  const [navMenuOpen, setNavMenuOpen] = useState(false);
  const [chatMode, setChatMode] = useState(() =>
    typeof window !== "undefined" && window.location.pathname.startsWith("/sell/v2")
  );
  const unreadCount = useUnreadCount(auth?.user?.id || null);

  // Auto-advance through demo stages.
  useEffect(() => {
    if (!isDemo || view !== "journey") return;
    const seqIdx = DEMO_SEQUENCE.indexOf(journey?.currentStageIdx ?? 0);
    const duration = DEMO_DURATIONS[seqIdx];
    if (seqIdx < 0 || seqIdx >= DEMO_SEQUENCE.length - 1 || !duration) return;
    const t = setTimeout(() => goTo(DEMO_SEQUENCE[seqIdx + 1]), duration);
    return () => clearTimeout(t);
  }, [isDemo, view, journey?.currentStageIdx]); // eslint-disable-line

  // Persist any journey state changes — localStorage + Supabase sync.
  // Skipped in demo mode so we don't pollute real data.
  useEffect(() => {
    if (!journey || isDemo) return;
    saveJourney(journey);
    if (supabase && auth?.user?.id && journey.address) {
      supabase.from("seller_listings").upsert({
        id: journey.id,
        client_id: auth.user.id,
        broker_id: journey.broker_id || null,
        address: journey.address,
        property: journey.property || null,
        current_stage_idx: journey.currentStageIdx || 0,
        stages: journey.stages || {},
        verification: journey.verification || null,
        lat: journey.coords?.lat ?? null,
        lng: journey.coords?.lng ?? null,
        updated_at: new Date().toISOString(),
      }, { onConflict: "id" }).then(({ error }) => {
        if (error) {
          console.error("seller_listings sync failed:", error);
          track("seller_listing_sync_failed", { error: error.message, code: error.code, journey_id: journey.id });
        }
      });
    } else if (journey.address) {
      // Didn't even attempt the sync — log why, so "why isn't my listing
      // showing up server-side" is debuggable instead of a silent no-op.
      track("seller_listing_sync_skipped", {
        has_supabase: Boolean(supabase),
        has_user: Boolean(auth?.user?.id),
        journey_id: journey.id,
      });
    }
  }, [journey]); // eslint-disable-line

  // ─── Dashboard actions ─────────────────────────────────────────────────────
  const openListing = (id) => {
    const j = loadJourney(id);
    if (!j) return;
    setCurrentId(id);
    track("seller_listing_opened", { id, address: j.address });
    setState({
      view: j.verification ? "journey" : "confirm",
      journey: j,
    });
  };

  const newListing = () => {
    setCurrentId(null);
    track("seller_new_listing_started");
    setState({ view: "intake", journey: null });
  };

  const removeListing = (id) => {
    if (!window.confirm("Delete this listing? You'll lose all the work on it.")) return;
    deleteJourney(id);
    track("seller_listing_deleted", { id });
    setState({ view: "dashboard", journey: null });
  };

  // ─── Intake → Confirm ──────────────────────────────────────────────────────
  const onIntakeStarted = (address, property, coords) => {
    if (isDemo) {
      setState({ view: "confirm", journey: DEMO_JOURNEY });
      return;
    }
    const facts = property?.property?.[0]?.building?.rooms || {};
    const j = blankJourney({ address, property, coords: coords || null });
    setCurrentId(j.id);
    saveJourney(j);
    track("seller_journey_started", { id: j.id, address, beds: facts.beds, baths: facts.bathstotal });
    setState({ view: "confirm", journey: j });
  };

  // ─── Confirm → Journey ─────────────────────────────────────────────────────
  const onOwnershipConfirmed = (verification) => {
    const j = { ...journey, verification };
    setState({ view: "journey", journey: j });
  };

  // ─── Journey actions ───────────────────────────────────────────────────────
  const updateStage = (id, patch) =>
    setState((s) => ({ ...s, journey: patchStage(s.journey, id, patch) }));

  const goTo = (idx) => {
    const clamped = Math.max(0, Math.min(STAGE_COUNT - 1, idx));
    setState((s) => ({ ...s, journey: { ...s.journey, currentStageIdx: clamped } }));
    track("seller_stage_view", { stage: STAGES[clamped].id, idx: clamped });
  };

  const advance = () => {
    const cur = STAGES[journey.currentStageIdx];
    const curState = getStageState(journey, cur.id);
    // Stages with a genuine internal gate (disclosures, MLS) must reach
    // "done" themselves — don't let this shared button force-complete them.
    if (cur.requiresManualDone && curState.status !== "done") return;
    setState((s) => {
      const j2 = curState.status === "done" ? s.journey : patchStage(s.journey, cur.id, { status: "done" });
      const nextIdx = Math.min(STAGE_COUNT - 1, s.journey.currentStageIdx + 1);
      return { ...s, journey: { ...j2, currentStageIdx: nextIdx } };
    });
    track("seller_stage_advanced", { stage: cur.id });
  };

  const exitToDashboard = () => {
    setCurrentId(null);
    setState({ view: "dashboard", journey: null });
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  const homeNav = () => (window.location.href = "/");

  if (view === "dashboard") {
    return (
      <>
        <style>{SELLER_CSS}</style>
        <Dashboard
          listings={listJourneys()}
          onOpen={openListing}
          onNew={newListing}
          onDelete={removeListing}
          onHome={homeNav}
        />
      </>
    );
  }

  if (view === "intake") {
    return (
      <>
        <style>{SELLER_CSS}</style>
        <Intake
          onStart={onIntakeStarted}
          onHome={() => setState({ view: "dashboard", journey: null })}
          isDemo={isDemo}
        />
      </>
    );
  }

  if (view === "confirm") {
    return (
      <>
        <style>{SELLER_CSS}</style>
        <OwnershipConfirm
          journey={journey}
          onConfirm={onOwnershipConfirmed}
          onCancel={exitToDashboard}
          isDemo={isDemo}
        />
      </>
    );
  }

  // view === "journey"
  const currentIdx = journey.currentStageIdx;
  const currentStage = STAGES[currentIdx];
  const currentState = getStageState(journey, currentStage.id);
  const StageBody = currentStage.Component;
  const advanceBlocked = currentStage.requiresManualDone && currentState.status !== "done";

  const brokerId = journey?.broker_id || null;
  const clientId = auth?.user?.id || null;

  return (
    <div className={`s-app${chatMode ? " s-app-chat" : ""}`}>
      <style>{SELLER_CSS}</style>

      {/* Demo mode banner + countdown bar */}
      {isDemo && (() => {
        const seqIdx = DEMO_SEQUENCE.indexOf(journey?.currentStageIdx ?? 0);
        const duration = seqIdx >= 0 ? DEMO_DURATIONS[seqIdx] : 0;
        return (
          <>
            <style>{`@keyframes demoProgress{from{width:0%}to{width:100%}}`}</style>
            <div style={{ background: "#1c5448", color: "#7ecfb0", fontFamily: "'DM Mono',monospace", fontSize: 11, letterSpacing: "0.1em", textAlign: "center", padding: "5px 0", flexShrink: 0 }}>
              DEMO · 6zero.ai · /sell?demo=1
            </div>
            {duration > 0 && (
              <div style={{ height: 2, background: "#0e2218", overflow: "hidden", flexShrink: 0 }}>
                <div key={journey?.currentStageIdx} style={{ height: "100%", background: "#7ecfb0", animation: `demoProgress ${duration}ms linear forwards` }} />
              </div>
            )}
          </>
        );
      })()}

      {/* Messages slide panel */}
      {messagesOpen && (
        <div style={{ position: "fixed", inset: 0, zIndex: 400, display: "flex" }} onClick={() => setMessagesOpen(false)}>
          <div style={{ flex: 1, background: "rgba(15,25,20,0.4)" }} />
          <div style={{ width: 360, background: "#fff", boxShadow: "-8px 0 40px rgba(0,0,0,0.15)", display: "flex", flexDirection: "column", height: "100%" }} onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: "14px 18px", borderBottom: "1px solid #f0f4f2", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
              <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 12, fontWeight: 700, color: "#1c2b25", letterSpacing: "0.1em" }}>MESSAGES</span>
              <button onClick={() => setMessagesOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "#9bbfb2", fontSize: 18, lineHeight: 1 }}>×</button>
            </div>
            <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
              <MessageThread brokerId={brokerId} clientId={clientId} myRole="seller" clientName="Your broker" />
            </div>
          </div>
        </div>
      )}

      <header className="s-top">
        <div className="s-brand" onClick={exitToDashboard}>
          <Logo />
          <div className="s-brand-name">6zero.ai</div>
        </div>
        <div className="s-addr-pill" title={journey.address}>{journey.address}</div>
        <button type="button" className="s-back ml-auto" onClick={exitToDashboard}>← my listings</button>
        <button
          type="button"
          className={`s-chat-toggle${chatMode ? " active" : ""}`}
          onClick={() => setChatMode((v) => !v)}
          title="Toggle chat view"
        >{chatMode ? "switch to carousel" : "switch to chat"}</button>
        <div style={{ position: "relative" }}>
          <button type="button" className="s-back" onClick={() => setNavMenuOpen((v) => !v)} title="More" style={{ position: "relative" }}>
            ☰
            {unreadCount > 0 && (
              <span style={{ position:"absolute", top:-4, right:-6, minWidth:16, height:16, background:"#c0354a", color:"#fff", borderRadius:8, fontSize:9, fontFamily:"'DM Mono',monospace", fontWeight:700, display:"flex", alignItems:"center", justifyContent:"center", padding:"0 4px", lineHeight:1 }}>
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </button>
          {navMenuOpen && (
            <>
              <div onClick={() => setNavMenuOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 99 }} />
              <div style={{ position: "absolute", top: "calc(100% + 6px)", right: 0, background: "#fff", border: "1px solid #d8ede6", borderRadius: 10, padding: 6, zIndex: 100, minWidth: 170, boxShadow: "0 8px 24px rgba(0,0,0,0.12)", display: "flex", flexDirection: "column", gap: 2 }}>
                <button type="button" className="s-back" style={{ textAlign: "left", border: "none" }} onClick={() => (window.location.href = "/buy")}>Browse homes</button>
                <button type="button" className="s-back" style={{ textAlign: "left", border: "none" }} onClick={() => (window.location.href = "/marketplace")}>Services</button>
                <button type="button" className="s-back" style={{ textAlign: "left", border: "none", position: "relative" }} onClick={() => { setMessagesOpen(true); setNavMenuOpen(false); }}>
                  Messages {unreadCount > 0 && `(${unreadCount > 9 ? "9+" : unreadCount})`}
                </button>
                <button type="button" className="s-back" style={{ textAlign: "left", border: "none" }} onClick={() => (window.location.href = "/account")}>Account</button>
                <button type="button" className="s-back" style={{ textAlign: "left", border: "none" }} onClick={homeNav}>Home</button>
              </div>
            </>
          )}
        </div>
      </header>

      {chatMode ? (
        <ChatJourney
          journey={journey}
          currentIdx={currentIdx}
          onBack={() => goTo(currentIdx - 1)}
          onAdvance={advance}
          onJump={goTo}
          onUpdate={(patch) => updateStage(currentStage.id, patch)}
          onUpdateStage={updateStage}
          advanceDisabled={advanceBlocked}
        />
      ) : (
        <>
          <Stepper journey={journey} currentIdx={currentIdx} onJump={goTo} />
          <StageShell
            stage={currentStage}
            status={currentState.status}
            currentIdx={currentIdx}
            total={STAGE_COUNT}
            onBack={() => goTo(currentIdx - 1)}
            onAdvance={advance}
            advanceDisabled={advanceBlocked}
          >
            <StageBody
              state={currentState}
              journey={journey}
              update={(patch) => updateStage(currentStage.id, patch)}
            />
          </StageShell>
          <AskExpert
            journey={journey}
            stage={currentStage}
            onAdvance={advance}
            onBack={() => goTo(currentIdx - 1)}
            onJump={goTo}
            onUpdateStage={updateStage}
          />
        </>
      )}
    </div>
  );
}
